import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { todayEastern, toEasternTime } from '../utils/time';
import { useTableControls } from '../hooks/useTableControls';
import SearchBox from '../components/SearchBox';
import StatusTabs from '../components/StatusTabs';
import SortableTh from '../components/SortableTh';
import Pagination from '../components/Pagination';
import Icon from '../components/Icon';

const STATUS_TABS = [
  { value: 'all', label: 'All entries' },
  { value: 'ok', label: 'Succeeded' },
  { value: 'fail', label: 'Failed' },
];

export default function AuditLogPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(todayEastern());
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback((d = date) =>
    api.getAuditLog(`?date=${d}`)
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => toast('Failed to load audit log', 'error'))
      .finally(() => setLoading(false)), [date, toast]);

  useEffect(() => { load(date); }, [date, load]);

  const tc = useTableControls({
    items,
    searchFields: (a) => [a.action_type, a.lambda_function, a.error_message, a.output_summary],
    statusOf: (a) => (a.success ? 'ok' : 'fail'),
    sorters: {
      timestamp: (a) => a.timestamp || '',
      action_type: (a) => a.action_type || '',
      status: (a) => (a.success ? 'ok' : 'fail'),
      duration_ms: (a) => a.duration_ms || 0,
    },
    defaultSort: 'timestamp',
    defaultDir: 'desc',
    pageSize: 15,
  });

  return (
    <>
      <div className="page-header">
        <h1>Audit Log</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="date"
            className="form-input"
            style={{ width: 160 }}
            value={date}
            onChange={e => { setDate(e.target.value); setLoading(true); load(e.target.value); }}
          />
        </div>
      </div>

      {!loading && items.length > 0 && (
        <section className="summary-bar">
          <div className="summary-item">
            <span className="summary-value">{items.length}</span>
            <span className="summary-label">Total entries</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item success">
            <span className="summary-value">{tc.counts.ok || 0}</span>
            <span className="summary-label">Succeeded</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item danger">
            <span className="summary-value">{tc.counts.fail || 0}</span>
            <span className="summary-label">Failed</span>
          </div>
          {lastUpdated && (
            <div className="summary-updated">
              <span className="live-dot" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </section>
      )}

      <section className="list-panel">
        <div className="panel-toolbar">
          <div className="panel-toolbar-title">
            <h2>Entries for {date}</h2>
            <span>{tc.sorted.length} records</span>
          </div>
          <div className="panel-toolbar-actions">
            <SearchBox value={tc.search} onChange={tc.setSearch} placeholder="Search audit log…" />
            <button type="button" className="icon-btn" onClick={() => load()} title="Refresh">
              <Icon name="refresh" />
            </button>
          </div>
        </div>

        <StatusTabs tabs={STATUS_TABS} active={tc.activeStatus} onChange={tc.setActiveStatus} counts={tc.counts} total={items.length} />

        <div className="table-wrap">
          {loading ? (
            <div className="empty-state"><p>Loading…</p></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <p>No log entries for {date}</p>
            </div>
          ) : tc.paginated.length === 0 ? (
            <div className="empty-state"><p>No entries match your search or filters.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableTh col="timestamp" label="Time" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <SortableTh col="action_type" label="Action" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Function</th>
                  <SortableTh col="status" label="Result" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <SortableTh col="duration_ms" label="Duration" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {tc.paginated.map(a => (
                  <tr key={a.log_id}>
                    <td className="td-mono">{toEasternTime(a.timestamp)}</td>
                    <td style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--navy)' }}>{a.action_type}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{a.lambda_function?.replace('hca-', '')}</td>
                    <td>
                      <span className={`badge badge-dot ${a.success ? 'badge-published' : 'badge-failed'}`}>
                        {a.success ? 'OK' : 'FAIL'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                      {a.duration_ms ? `${a.duration_ms}ms` : '—'}
                    </td>
                    <td className="td-truncate" style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                      {a.error_message || a.output_summary || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pagination
          pageSize={tc.pageSize}
          setPageSize={tc.setPageSize}
          currentPage={tc.currentPage}
          setCurrentPage={tc.setCurrentPage}
          totalPages={tc.totalPages}
          pageStart={tc.pageStart}
          pageEnd={tc.pageEnd}
          total={tc.sorted.length}
          paginationItems={tc.paginationItems}
        />
      </section>
    </>
  );
}
